import { Button, Select } from '@cimplico/components';
import { useEffect, useState } from 'react';
import { toastNotification } from '../../../../../../components/toast/Toast';
import { useIsActiveSubscription } from '../../../../../../hooks/use-is-active-subscription';
import { useFYICabinets } from '../../../../../../queries/use-fyi-cabinets';
import { useUpdateFYIConfig } from '../../../../../../queries/use-update-fyi-config';
import { Cabinet } from '../../../../../../types/fyi-docs';

type FirmFYICabinetFormProps = {
    defaultCabinet?: Cabinet;
    onCancel?: () => void;
};

function FirmFYICabinetForm({ defaultCabinet, onCancel }: FirmFYICabinetFormProps): JSX.Element {
    const cabinetsQuery = useFYICabinets({ page: 1, limit: 100 });
    const updateConfigQuery = useUpdateFYIConfig();
    const activeSubscription = useIsActiveSubscription();
    const [selectedCabinet, setSelectedCabinet] = useState<{ value?: Cabinet; error?: string }>({ value: defaultCabinet });

    useEffect(() => {
        const cabinets = cabinetsQuery.data?.cabinets ?? [];
        const workpaperCabinet = cabinets.find((c) => /workpapers{0,1}/.test(c.name.toLowerCase()));

        if (!selectedCabinet.value) {
            setSelectedCabinet({ value: workpaperCabinet });
        }
    }, [cabinetsQuery.data?.cabinets, selectedCabinet.value]);

    function handleCabinetSelect(id?: number): void {
        const cabinet = cabinetsQuery.data?.cabinets?.find((c) => c.id === id);
        setSelectedCabinet({ value: cabinet });
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        if (!selectedCabinet.value) {
            setSelectedCabinet({ error: 'A cabinet is required' });
            return;
        }

        // Submit form
        updateConfigQuery.mutate(
            {
                cabinet: {
                    id: selectedCabinet.value.id,
                    name: selectedCabinet.value.name,
                },
            },
            {
                onSuccess: () => {
                    toastNotification('success', 'Successfully saved cabinet selection!');
                    if (onCancel) {
                        onCancel();
                    }
                },
                onError: () => {
                    toastNotification('error', 'Unable to save cabinet selection!');
                },
            }
        );
    }

    if ((cabinetsQuery.isError || !cabinetsQuery.isSuccess) && !cabinetsQuery.isLoading) {
        return <p>Error!</p>;
    }

    const cabinets = cabinetsQuery.data?.cabinets ?? [];

    return (
        <div>
            <form className="mt-4" onSubmit={handleSubmit}>
                <Select
                    options={cabinets.map((c) => ({
                        label: c.name,
                        value: c.id,
                        isSelected: selectedCabinet.value?.id === c.id,
                    }))}
                    onChange={(opts): void => handleCabinetSelect(+opts[0]?.value)}
                    title="Select a cabinet"
                    errorMessage={selectedCabinet.error}
                    isDisabled={!activeSubscription}
                />

                <div className="mt-4">
                    {/* <Select
                        options={categories.map((c) => ({
                            label: c.name,
                            value: c.id,
                            isSelected: selectedCategory.value?.id === c.id,
                        }))}
                        onChange={(opts): void => handleCategorySelect(+opts[0]?.value)}
                        title="Select a category"
                        isDisabled={!selectedCabinet}
                        disabledHint={!selectedCabinet ? 'Please select a cabinet.' : undefined}
                        errorMessage={selectedCategory.error}
                    /> */}
                    <div className="mt-2 flex items-center justify-end gap-2">
                        {onCancel && <Button type="hollow" label="Cancel" onClick={onCancel} />}
                        <Button
                            isSubmit
                            type="primary"
                            size="sm"
                            label={updateConfigQuery.isLoading ? 'Saving...' : 'Save'}
                            isLoading={updateConfigQuery.isLoading}
                            disabled={!activeSubscription}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
}
export default FirmFYICabinetForm;
